'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetMarkets, usePrefetchGetProposals, usePrefetchGetWinners } from '@morfi/backend-sdk-v2/client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { NavigationList, NavigationListItem } from '@/components/ui/navigation-list';
import { categoryNavTargetBase, isCategoryFilterListingSurface } from '@/config/navigation';
import { categoryNameToId, PROPOSAL_CATEGORIES } from '@/config/proposal-options';
import { prefetchRouteFull } from '@/hooks/use-intent-route-prefetch';
import { usePathname } from '@/i18n/navigation';
import { useAppRouter } from '@/lib/network/use-app-router';
import { warmArgsForBase } from './feed-intent-prefetch';

// Category is sent as the canonical slug (`categoryNameToId`) because every
// feed converts the URL name → slug at the wire boundary; sending the raw
// display name would key a different (missed) entry. The per-surface base args
// live in `./feed-intent-prefetch` (shared with the header tab nav) so the
// byte-match payloads never drift between the two hover-prefetch surfaces.
const VALID_CATEGORIES: ReadonlySet<string> = new Set(PROPOSAL_CATEGORIES.map(c => c.name));
export function CategoryNav() {
  const $ = _c(42);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const tNav = useTranslations("nav");
  const tCat = useTranslations("categories");
  const tHeader = useTranslations("header");
  const prefetchMarkets = usePrefetchGetMarkets();
  const prefetchWinners = usePrefetchGetWinners();
  const prefetchProposals = usePrefetchGetProposals();
  let activeCategory;
  let base;
  let isListingSurface;
  let prefetchFor;
  let t0;
  if ($[0] !== pathname || $[1] !== prefetchMarkets || $[2] !== prefetchProposals || $[3] !== prefetchWinners || $[4] !== searchParams) {
    base = categoryNavTargetBase(pathname);
    let t1;
    if ($[10] !== pathname) {
      t1 = isCategoryFilterListingSurface(pathname);
      $[10] = pathname;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    isListingSurface = t1;
    let t2;
    if ($[12] !== isListingSurface || $[13] !== searchParams) {
      const categoryParam = searchParams.get("category");
      t2 = isListingSurface && categoryParam !== null && VALID_CATEGORIES.has(categoryParam) ? categoryParam : null;
      $[12] = isListingSurface;
      $[13] = searchParams;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    activeCategory = t2;
    prefetchFor = base === "/winners" ? prefetchWinners : base === "/proposals" ? prefetchProposals : prefetchMarkets;
    t0 = warmArgsForBase(base);
    $[0] = pathname;
    $[1] = prefetchMarkets;
    $[2] = prefetchProposals;
    $[3] = prefetchWinners;
    $[4] = searchParams;
    $[5] = activeCategory;
    $[6] = base;
    $[7] = isListingSurface;
    $[8] = prefetchFor;
    $[9] = t0;
  } else {
    activeCategory = $[5];
    base = $[6];
    isListingSurface = $[7];
    prefetchFor = $[8];
    t0 = $[9];
  }
  const warmBase = t0;
  const router = useAppRouter();
  let t1;
  if ($[15] !== base || $[16] !== isListingSurface || $[17] !== prefetchFor || $[18] !== router || $[19] !== warmBase) {
    t1 = function prefetchAll() {
      if (!isListingSurface) {
        return;
      }
      prefetchRouteFull(router, base);
      prefetchFor(warmBase);
    };
    $[15] = base;
    $[16] = isListingSurface;
    $[17] = prefetchFor;
    $[18] = router;
    $[19] = warmBase;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  const prefetchAll = t1;
  let t2;
  if ($[21] !== tHeader) {
    t2 = tHeader("categoryNavigation");
    $[21] = tHeader;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  const t3 = isListingSurface && activeCategory === null;
  let t4;
  if ($[23] !== tNav) {
    t4 = tNav("allCategories");
    $[23] = tNav;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== base || $[26] !== prefetchAll || $[27] !== t3 || $[28] !== t4) {
    t5 = <NavigationListItem href={base} active={t3} prefetch={false} onMouseEnter={prefetchAll} onFocus={prefetchAll} onPointerDown={prefetchAll}>{t4}</NavigationListItem>;
    $[25] = base;
    $[26] = prefetchAll;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== activeCategory || $[31] !== base || $[32] !== isListingSurface || $[33] !== prefetchFor || $[34] !== router || $[35] !== tCat || $[36] !== warmBase) {
    t6 = PROPOSAL_CATEGORIES.map(cat => {
      const categoryHref = `${base}?category=${encodeURIComponent(cat.name)}`;
      const prefetchCategory = function prefetchCategory() {
        if (!isListingSurface) {
          return;
        }
        prefetchRouteFull(router, categoryHref);
        prefetchFor({
          ...warmBase,
          category: categoryNameToId(cat.name)
        });
      };
      return <NavigationListItem key={cat.id} href={categoryHref} active={activeCategory === cat.name} prefetch={false} onMouseEnter={prefetchCategory} onFocus={prefetchCategory} onPointerDown={prefetchCategory}>{tCat(cat.id as keyof IntlMessages['categories'])}</NavigationListItem>;
    });
    $[30] = activeCategory;
    $[31] = base;
    $[32] = isListingSurface;
    $[33] = prefetchFor;
    $[34] = router;
    $[35] = tCat;
    $[36] = warmBase;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== t2 || $[39] !== t5 || $[40] !== t6) {
    t7 = <NavigationList aria-label={t2} gradientFrom="from-background-secondary" edgeEffect="none" scrollClassName="pl-4 sm:pl-6 lg:pl-0">{t5}{t6}</NavigationList>;
    $[38] = t2;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  return t7;
}